import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#buildAltIndex();
    document.addEventListener('change', (e) => this.#handleColorSwatchChange(e), { signal });
    document.addEventListener('click', (e) => this.#handleColorSwatchClick(e), { signal });
  }

  #controller = new AbortController();

  /**
   * When set, the next variant update will be skipped so the gallery
   * keeps showing the color-matched image instead of resetting.
   * Cleared automatically when a non-color variant changes.
   */
  static #skipNextVariantUpdate = false;

  /** @type {Map<string, string>} Maps normalized alt text to slide-id */
  #altIndex = new Map();

  /**
   * Builds an index mapping image alt text (lowercased, hyphenated) to slide IDs.
   */
  #buildAltIndex() {
    this.#altIndex.clear();
    const slides = this.slideshow?.refs?.slides || [];
    for (const slide of slides) {
      const slideId = slide.getAttribute('slide-id');
      if (!slideId) continue;
      const img = slide.querySelector('img');
      if (!img) continue;
      const alt = (img.alt || '').toLowerCase().trim().replace(/[\s_]+/g, '-');
      if (alt) {
        this.#altIndex.set(alt, slideId);
      }
    }
  }

  /**
   * Finds the best matching slide for a given color name.
   * @param {string} colorName - The color/fabric name to match.
   * @returns {string|null} The matching slide ID or null.
   */
  #findMatchingSlide(colorName) {
    const normalized = colorName.toLowerCase().trim().replace(/[\s_]+/g, '-');

    // Exact match
    if (this.#altIndex.has(normalized)) {
      return this.#altIndex.get(normalized);
    }

    // Partial match: alt starts with normalized or vice versa
    for (const [alt, slideId] of this.#altIndex) {
      if (alt.startsWith(normalized) || normalized.startsWith(alt)) {
        return slideId;
      }
    }

    // Match by first word only (e.g. "black" matches "black-chenille")
    const colorPart = normalized.split('-')[0];
    if (colorPart.length >= 2) {
      for (const [alt, slideId] of this.#altIndex) {
        if (alt.startsWith(colorPart + '-')) {
          return slideId;
        }
      }
    }

    return null;
  }

  /**
   * Handles color swatch click events for custom color picker.
   */
  #handleColorSwatchClick(event) {
    const target = event.target.closest('[data-fabric-color-picker] .color-grid-item');
    if (!target) return;

    const colorName = target.querySelector('input[data-color-name]')?.getAttribute('data-color-name');
    if (!colorName) return;

    MediaGallery.#skipNextVariantUpdate = true;
    this.#navigateToColor(colorName);
  }

  /**
   * Handles variant picker color swatch changes and custom color picker changes.
   * Also clears the skip flag when a non-color variant changes.
   */
  #handleColorSwatchChange(event) {
    const input = event.target;
    if (!input) return;

    // Handle custom fabric color picker inputs
    if (input.classList.contains('custom-color-option')) {
      const colorName = input.getAttribute('data-color-name') || input.value;
      if (colorName) {
        MediaGallery.#skipNextVariantUpdate = true;
        this.#navigateToColor(colorName);
      }
      return;
    }

    // For select dropdowns (non-color), clear the skip flag
    if (input.tagName === 'SELECT') {
      MediaGallery.#skipNextVariantUpdate = false;
      return;
    }

    // Handle variant-picker color/colour swatches
    const fieldset = input.closest('fieldset');
    if (!fieldset) return;

    const isColorOption = fieldset.classList.contains('fieldset-color-swatch');
    if (!isColorOption) {
      // Non-color variant (size, etc.) changed — allow normal gallery replacement
      MediaGallery.#skipNextVariantUpdate = false;
      return;
    }

    const selectedValue = input.value;
    if (!selectedValue) return;

    MediaGallery.#skipNextVariantUpdate = true;
    this.#navigateToColor(selectedValue);
  }

  /**
   * Navigates the gallery to the slide matching the given color name.
   */
  #navigateToColor(colorName) {
    this.#buildAltIndex();

    const slideId = this.#findMatchingSlide(colorName);
    if (!slideId) return;

    if (this.slideshow) {
      this.slideshow.select({ id: slideId }, undefined, { animate: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery
   * with a new one — unless a color swatch navigation just happened,
   * in which case we keep the current gallery to preserve the image position.
   */
  #handleVariantUpdate = (event) => {
    if (MediaGallery.#skipNextVariantUpdate) {
      MediaGallery.#skipNextVariantUpdate = false;
      return;
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
